import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Chip,
    CircularProgress,
    Pagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Stack,
    Snackbar,
    Alert,
    Tooltip,
    Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { API_ENDPOINTS, getData, postData, api } from "../../services/apiService.js";

/**
 * OWO-001 Phase 5 — Withdrawal Queue.
 *
 * Tabs filter by status. Per-row actions transition state:
 *   requested → Claim → processing
 *   processing → Complete (with bank_method + UTR) → completed
 *   processing → Fail (with reason) → failed (auto-refund)
 *
 * Top-right: TDS Export — downloads Form 26Q-aligned CSV for a quarter.
 */

const STATUS_TABS = [
    { key: "requested", label: "Pending" },
    { key: "processing", label: "Processing" },
    { key: "completed", label: "Completed" },
    { key: "failed", label: "Failed" },
    { key: "cancelled", label: "Cancelled" },
    { key: "all", label: "All" },
];

const STATUS_CHIP = {
    requested: { label: "Requested", color: "warning" },
    processing: { label: "Processing", color: "info" },
    completed: { label: "Completed", color: "success" },
    failed: { label: "Failed", color: "error" },
    cancelled: { label: "Cancelled", color: "default" },
};

const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(Number(n) || 0);

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
};

function Withdrawals() {
    const [tab, setTab] = useState("requested");
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", text: "" });

    // Action dialogs
    const [completeDialog, setCompleteDialog] = useState({ open: false, row: null, bank_method: "NEFT", bank_reference: "" });
    const [failDialog, setFailDialog] = useState({ open: false, row: null, reason: "" });
    const [tdsDialog, setTdsDialog] = useState({ open: false, quarter: "Q1-FY26-27" });

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit };
            if (tab !== "all") params.status = tab;
            const res = await getData(API_ENDPOINTS.adminWithdrawalsList, params);
            if (res?.statusCode === 200) {
                setRows(res.result?.requests ?? []);
                setTotal(res.result?.total ?? 0);
            } else {
                setSnackbar({ open: true, severity: "error", text: res?.message || "Failed to load" });
                setRows([]);
                setTotal(0);
            }
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                text: e?.response?.data?.message || e.message,
            });
        } finally {
            setLoading(false);
        }
    }, [tab, page, limit]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const handleTabChange = (_, value) => {
        setTab(value);
        setPage(1);
    };

    const showOk = (text) => setSnackbar({ open: true, severity: "success", text });
    const showErr = (e) =>
        setSnackbar({
            open: true,
            severity: "error",
            text: e?.response?.data?.message || e?.message || "Action failed",
        });

    const handleClaim = async (row) => {
        try {
            const url = API_ENDPOINTS.adminWithdrawalClaim.replace(":id", row.request_id);
            const res = await postData(url, {});
            if (res?.statusCode === 200) {
                showOk(
                    `Claimed ${row.request_ref}. Wallet debited ₹${row.requested_amount}. Now execute the bank transfer and complete with the UTR.`
                );
                fetchRows();
            } else {
                showErr({ message: res?.message });
            }
        } catch (e) {
            showErr(e);
        }
    };

    const handleComplete = async () => {
        const { row, bank_method, bank_reference } = completeDialog;
        try {
            const url = API_ENDPOINTS.adminWithdrawalComplete.replace(":id", row.request_id);
            const res = await postData(url, { bank_method, bank_reference });
            if (res?.statusCode === 200) {
                showOk(`Completed ${row.request_ref} with ${bank_method}/${bank_reference}.`);
                setCompleteDialog({ open: false, row: null, bank_method: "NEFT", bank_reference: "" });
                fetchRows();
            } else {
                showErr({ message: res?.message });
            }
        } catch (e) {
            showErr(e);
        }
    };

    const handleFail = async () => {
        const { row, reason } = failDialog;
        try {
            const url = API_ENDPOINTS.adminWithdrawalFail.replace(":id", row.request_id);
            const res = await postData(url, { failure_reason: reason });
            if (res?.statusCode === 200) {
                showOk(`Failed ${row.request_ref}; ₹${row.requested_amount} refunded to owner wallet.`);
                setFailDialog({ open: false, row: null, reason: "" });
                fetchRows();
            } else {
                showErr({ message: res?.message });
            }
        } catch (e) {
            showErr(e);
        }
    };

    const handleTdsExport = async () => {
        const { quarter } = tdsDialog;
        try {
            // Fetch as raw response to get CSV blob; api.get returns axios response.
            const r = await api.get(API_ENDPOINTS.adminWithdrawalsTdsExport, {
                params: { quarter, format: "csv" },
                responseType: "blob",
            });
            const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `tds_194o_${quarter}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showOk(`TDS export for ${quarter} downloaded.`);
            setTdsDialog({ open: false, quarter: tdsDialog.quarter });
        } catch (e) {
            showErr(e);
        }
    };

    const T = { teal:"#007C84", tealDk:"#016268", tealLt:"#E0F4F5", bg:"#F7FAFC", border:"#E2E8F0", text:"#0F172A", muted:"#64748B" };

    return (
        <Box sx={{ bgcolor: T.bg, minHeight:"100vh", pb:4 }}>
            {/* Hero header */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2, boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Box>
                    <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Withdrawal Queue</Typography>
                    <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.5 }}>Claim → execute bank transfer → mark Complete with UTR</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setTdsDialog({ ...tdsDialog, open: true })}
                        sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, color:"#fff", borderColor:"rgba(255,255,255,0.4)", bgcolor:"rgba(255,255,255,0.1)", "&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
                        TDS Export
                    </Button>
                    <Button variant="outlined" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />} onClick={fetchRows} disabled={loading}
                        sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, color:"#fff", borderColor:"rgba(255,255,255,0.4)", bgcolor:"rgba(255,255,255,0.1)", "&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
                        Refresh
                    </Button>
                </Stack>
            </Box>

            <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
                <CardContent sx={{ pb:"0 !important" }}>
                    {/* Pill tab toggle */}
                    <Box sx={{ display:"flex", gap:0.5, bgcolor:T.bg, border:`1px solid ${T.border}`, borderRadius:"12px", p:0.5, width:"fit-content", mb:2 }}>
                        {STATUS_TABS.map(t => (
                            <Box key={t.key} onClick={() => handleTabChange(null, t.key)} sx={{
                                px:1.8, py:0.7, borderRadius:"9px", cursor:"pointer",
                                bgcolor: tab === t.key ? T.teal : "transparent",
                                color:   tab === t.key ? "#fff" : T.muted,
                                fontWeight: tab === t.key ? 700 : 500, fontSize:13,
                                transition:"all 0.14s", "&:hover":{ bgcolor: tab===t.key ? T.teal : T.tealLt, color: tab===t.key ? "#fff" : T.teal },
                                fontFamily:"'Plus Jakarta Sans',sans-serif",
                            }}>{t.label}</Box>
                        ))}
                    </Box>

                    <TableContainer sx={{ px:0 }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor:T.bg }}>
                                <TableRow sx={{ "& th":{ fontWeight:700, fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", py:1.5, borderBottom:`2px solid ${T.border}` } }}>
                                    <TableCell>Ref</TableCell>
                                    <TableCell>Owner</TableCell>
                                    <TableCell>Destination</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="right">TDS</TableCell>
                                    <TableCell align="right">Net</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Requested</TableCell>
                                    <TableCell>UTR</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <Typography color="text.secondary" py={3}>
                                                No withdrawal requests in this view.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {rows.map((r) => {
                                    const chip = STATUS_CHIP[r.status] || { label: r.status, color: "default" };
                                    return (
                                        <TableRow key={r.request_id} hover>
                                            <TableCell>
                                                <Typography fontWeight={600}>{r.request_ref}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    #{r.request_id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{r.owner_name || `Owner #${r.owner_id}`}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {r.owner_mobile}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {r.destination ? (
                                                    <Box>
                                                        {r.destination.type === "bank_account" ? (
                                                            <>
                                                                <Typography variant="body2">
                                                                    {r.destination.bank_name || "Bank"} {r.destination.account_masked}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {r.destination.ifsc_code}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <Typography variant="body2">UPI: {r.destination.vpa_address}</Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{fmtINR(r.requested_amount)}</TableCell>
                                            <TableCell align="right">{fmtINR(r.tds_amount)}</TableCell>
                                            <TableCell align="right">{r.net_amount != null ? fmtINR(r.net_amount) : "—"}</TableCell>
                                            <TableCell>
                                                <Chip label={chip.label} color={chip.color} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={`Requested ${fmtDate(r.requested_at)}`} placement="top">
                                                    <Typography variant="caption">{fmtDate(r.requested_at)}</Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {r.bank_reference ? (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {r.bank_method}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                                            {r.bank_reference}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    {r.status === "requested" && (
                                                        <Button size="small" variant="contained" onClick={() => handleClaim(r)}>
                                                            Claim
                                                        </Button>
                                                    )}
                                                    {r.status === "processing" && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                onClick={() =>
                                                                    setCompleteDialog({ open: true, row: r, bank_method: "NEFT", bank_reference: "" })
                                                                }
                                                            >
                                                                Complete
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={() => setFailDialog({ open: true, row: r, reason: "" })}
                                                            >
                                                                Fail
                                                            </Button>
                                                        </>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination page={page} count={totalPages} onChange={(_, p) => setPage(p)} color="primary" size="small" />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Complete dialog */}
            <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ ...completeDialog, open: false })} fullWidth maxWidth="sm">
                <DialogTitle>Complete withdrawal — {completeDialog.row?.request_ref}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Record the bank reference (UTR) returned by your bank's portal after the transfer.
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            select
                            label="Bank method"
                            value={completeDialog.bank_method}
                            onChange={(e) => setCompleteDialog({ ...completeDialog, bank_method: e.target.value })}
                            size="small"
                        >
                            {["NEFT", "IMPS", "RTGS", "UPI"].map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Bank reference (UTR)"
                            value={completeDialog.bank_reference}
                            onChange={(e) => setCompleteDialog({ ...completeDialog, bank_reference: e.target.value })}
                            size="small"
                            placeholder="e.g. SBIN0040001234"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteDialog({ ...completeDialog, open: false })}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleComplete}
                        disabled={!completeDialog.bank_reference?.trim()}
                    >
                        Mark Complete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Fail dialog */}
            <Dialog open={failDialog.open} onClose={() => setFailDialog({ ...failDialog, open: false })} fullWidth maxWidth="sm">
                <DialogTitle>Fail withdrawal — {failDialog.row?.request_ref}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Marking failed will refund ₹{failDialog.row?.requested_amount} back to the owner's wallet.
                    </Typography>
                    <TextField
                        label="Failure reason"
                        value={failDialog.reason}
                        onChange={(e) => setFailDialog({ ...failDialog, reason: e.target.value })}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="e.g. Bank rejected: incorrect IFSC"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFailDialog({ ...failDialog, open: false })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleFail} disabled={!failDialog.reason?.trim()}>
                        Fail + Refund
                    </Button>
                </DialogActions>
            </Dialog>

            {/* TDS export dialog */}
            <Dialog open={tdsDialog.open} onClose={() => setTdsDialog({ ...tdsDialog, open: false })} fullWidth maxWidth="xs">
                <DialogTitle>Export Section 194-O TDS</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Downloads a Form 26Q-aligned CSV for completed withdrawals in the selected quarter. Failed/refunded rows are excluded.
                    </Typography>
                    <TextField
                        label="Quarter"
                        value={tdsDialog.quarter}
                        onChange={(e) => setTdsDialog({ ...tdsDialog, quarter: e.target.value })}
                        size="small"
                        fullWidth
                        helperText="Format: Q1-FY26-27 (Q1=Apr-Jun, Q4=Jan-Mar)"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTdsDialog({ ...tdsDialog, open: false })}>Cancel</Button>
                    <Button variant="contained" onClick={handleTdsExport} startIcon={<DownloadIcon />}>
                        Download CSV
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.text}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Withdrawals;
